import { tagged } from "@mirage/service-logging";
import { typeahead } from "../../types";

import type { TypeaheadCache } from "@mirage/service-typeahead-search/service/typeahead-cache";
import type { SectionIdUnion } from "@mirage/shared/search/section-tracking-record";

const logger = tagged("section-score");

// Configuration constants
const POSITION_WEIGHTS = [1.0, 0.8, 0.6, 0.4];
const MAX_SCORE_WEIGHT = 0.7;
const AVG_SCORE_WEIGHT = 0.3;
const PERSONALIZATION_FACTOR = 0.2;
const RECENCY_WEIGHT = 0.7;
const FREQUENCY_WEIGHT = 0.3;
const RECENCY_DECAY = 0.85; // Exponential decay factor for older interactions

interface SectionScoringState {
  query: string;
  mode: string;
  sourceResults: typeahead.ScoredResult[];
  sections: typeahead.PredicateSubject<typeahead.SectionId>[];
  sectionScores: Record<typeahead.SectionId, number>;
  cache: TypeaheadCache;
}

/**
 * Initialize scoring state with default values
 */
export function initializeScoringState(
  query: string,
  mode: string,
  sourceResults: typeahead.ScoredResult[],
  sections: typeahead.PredicateSubject<typeahead.SectionId>[],
  cache: TypeaheadCache,
  config: typeahead.Config,
): SectionScoringState {
  const FIND_SECTION_SCORE =
    config.prioritizeFindInNullStateExp === "TEST" ? 80 : 70;

  const SECTION_CONFIG: Record<typeahead.SectionId, number> = {
    [typeahead.SectionId.Ask]:
      config.prioritizeFindInNullStateExp === "TEST" ? 70 : 80,
    [typeahead.SectionId.AskDash]: 50,
    [typeahead.SectionId.Create]: 1, // NOTE: Will be used in Organize "mode" for P1
    [typeahead.SectionId.DraftFromSource]: 30,
    [typeahead.SectionId.DraftFromTemplate]: 50,
    [typeahead.SectionId.DraftFromTone]: 40,
    [typeahead.SectionId.Find]: FIND_SECTION_SCORE,
    [typeahead.SectionId.Organize]: 30,
    [typeahead.SectionId.Recent]: 90,
    [typeahead.SectionId.Recommended]: 50,
    [typeahead.SectionId.Suggested]: 50,
  } as const;

  return {
    query,
    mode,
    sourceResults,
    sections,
    sectionScores: {
      ...SECTION_CONFIG,
      [typeahead.SectionId.Find]: query.length > 0 ? 90 : FIND_SECTION_SCORE,
    },
    cache,
  };
}

// Apply base hybrid scoring to scores based on position and score
export function applyBaseHybridScoring(
  state: SectionScoringState,
): SectionScoringState {
  const newScores = { ...state.sectionScores };

  state.sections.forEach((section) => {
    // Filter out ask user and suggested queries, and non-matching sections
    const sectionResults = state.sourceResults.filter(
      (result) =>
        result.sectionId === section.id &&
        result.type !== typeahead.ResultType.AskUserQuery &&
        result.type !== typeahead.ResultType.SuggestedQuery &&
        result.type !== typeahead.ResultType.WriteUserQuery,
    );

    // If there are no results, set the score to 0
    if (sectionResults.length === 0) {
      newScores[section.id] = 0;
      return;
    }

    // Sort the results by score in descending order
    const sortedResults = [...sectionResults].sort((a, b) => b.score - a.score);

    // Calculate the weighted scores for each result
    const weightedScores = sortedResults.map((result, index) => {
      const positionWeight =
        index < POSITION_WEIGHTS.length
          ? POSITION_WEIGHTS[index]
          : POSITION_WEIGHTS[POSITION_WEIGHTS.length - 1];
      return result.score * positionWeight;
    });

    // Calculate the maximum and average scores
    const maxScore = Math.max(...weightedScores);
    const avgScore =
      weightedScores.reduce((sum, score) => sum + score, 0) /
      weightedScores.length;

    // Apply the base hybrid scoring formula
    newScores[section.id] =
      MAX_SCORE_WEIGHT * maxScore + AVG_SCORE_WEIGHT * avgScore;
  });

  return {
    ...state,
    sectionScores: newScores,
  };
}

// Apply personalization to scores based on user interaction history
export function applyPersonalization(
  state: SectionScoringState,
): SectionScoringState {
  const userSectionInteractions = state.cache.getSectionTracking();
  const newScores = { ...state.sectionScores };
  const now = Date.now();

  const allCounts = Object.values(userSectionInteractions).map(
    (section) => section.count,
  );
  const maxCount = allCounts.length > 0 ? Math.max(...allCounts) : 0;

  Object.keys(newScores).forEach((sectionId) => {
    const typedSectionId = sectionId as unknown as typeahead.SectionId;

    // Skip sections with zero score
    if (newScores[typedSectionId] === 0) return;

    const sectionInteractions =
      userSectionInteractions[typedSectionId as SectionIdUnion];

    // Skip if no interactions with this section
    if (!sectionInteractions || sectionInteractions.count === 0) return;

    // Calculate frequency score (normalized by max count)
    const frequencyScore =
      maxCount > 0 ? sectionInteractions.count / maxCount : 0;

    // Calculate recency score based on exponential decay of timestamps
    let recencyScore = 0;
    if (sectionInteractions.recentTracks.length > 0) {
      // Calculate weighted recency score with exponential decay
      recencyScore =
        sectionInteractions.recentTracks.reduce((score, track, index) => {
          const ageInHours = (now - track.timestamp) / (1000 * 60 * 60);
          const decayFactor = Math.pow(RECENCY_DECAY, ageInHours);
          return score + decayFactor * Math.pow(RECENCY_DECAY, index);
        }, 0) / sectionInteractions.recentTracks.length;
    }

    // Combine frequency and recency for personalization score
    const personalizationScore =
      FREQUENCY_WEIGHT * frequencyScore + RECENCY_WEIGHT * recencyScore;

    // Apply personalization boost
    if (personalizationScore > 0) {
      const boost = 1 + PERSONALIZATION_FACTOR * personalizationScore;
      newScores[typedSectionId] *= boost;
      logger.debug(
        `Applied personalization boost for section ${sectionId}: ${boost.toFixed(2)}`,
      );
    }
  });

  return {
    ...state,
    sectionScores: newScores,
  };
}

/**
 * Normalizes scores to ensure they're between 0 and 1
 */
export function normalizeScores(
  state: SectionScoringState,
): SectionScoringState {
  const scores = { ...state.sectionScores };

  const maxScore = Math.max(...Object.values(scores));

  if (maxScore > 0) {
    Object.keys(scores).forEach((sectionId) => {
      const typedSectionId = sectionId as unknown as typeahead.SectionId;
      scores[typedSectionId] =
        Math.round((scores[typedSectionId] / maxScore) * 1000) / 1000;
    });
  }

  return {
    ...state,
    sectionScores: scores,
  };
}

/**
 * Orchestrates the scoring pipeline by applying each factor in sequence
 */
export async function runScoringPipeline(
  state: SectionScoringState,
  pipeline: ((state: SectionScoringState) => SectionScoringState)[],
): Promise<Record<typeahead.SectionId, number>> {
  let finalState = state;
  for (const factorFn of pipeline) {
    finalState = factorFn(finalState);
  }

  return finalState.sectionScores;
}
